import {
  PatientConversationAssignmentEnum,
  PatientConversationDTO,
  PatientConversationEventSourceEnum,
  PatientConversationStatusEnum
} from "@vitable/asclepius-api-client-ts"

export interface ConversationHistoryEntry {
  key: string
  title: string
  /** Who did it, or the rule that did. */
  detail: string
  at: string
  /** The opening and closing rows bookend the timeline; everything between is a change. */
  kind: "start" | "change" | "end"
}

const AUTOMATIC = "Automatic"

/** Status and assignment events merged oldest first, worded for the history drawer. */
export function buildConversationHistory(conversation: PatientConversationDTO): Array<ConversationHistoryEntry> {
  const statusEntries = (conversation.status_events ?? []).map<ConversationHistoryEntry>((event, index) => ({
    key: `status-${index}-${event.created_at}`,
    title:
      event.from_status === null
        ? "Conversation started"
        : event.to_status === PatientConversationStatusEnum.Open
        ? "Unarchived"
        : event.to_status,
    detail: describeActor(event.actor?.full_name, event.source, event.reason),
    at: event.created_at,
    kind:
      event.from_status === null ? "start" : event.to_status === PatientConversationStatusEnum.Closed ? "end" : "change"
  }))

  const assignmentEntries = (conversation.assignment_events ?? []).map<ConversationHistoryEntry>((event, index) => ({
    key: `assignment-${index}-${event.created_at}`,
    title: describeAssignment(event.from_status, event.to_status, event.assignee?.full_name),
    detail: describeActor(event.actor?.full_name, event.source, event.reason),
    at: event.created_at,
    kind: "change"
  }))

  return [...statusEntries, ...assignmentEntries].sort((a, b) => a.at.localeCompare(b.at))
}

// Assigned → Assigned is a hand-off, so it reads as a reassignment
function describeAssignment(
  from: PatientConversationAssignmentEnum | null,
  to: PatientConversationAssignmentEnum,
  assigneeName: string | undefined
): string {
  if (to !== PatientConversationAssignmentEnum.Assigned || !assigneeName) return "Unassigned"
  return from === PatientConversationAssignmentEnum.Assigned
    ? `Reassigned to ${assigneeName}`
    : `Assigned to ${assigneeName}`
}

// A person's name speaks for itself; a rule's fixed reason explains what happened
function describeActor(actorName: string | undefined, source: PatientConversationEventSourceEnum, reason: string) {
  return source === PatientConversationEventSourceEnum.Rule || !actorName ? `${AUTOMATIC} · ${reason}` : actorName
}
